import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { pageColors } from "../mock";
import cultureMark from "../assets/culture_mark.png";
import {
  Upload,
  ArrowUpRight,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  Sparkles,
} from "lucide-react";

const stories = [
  {
    id: "art",
    category: "Art",
    title: "Slawn — a scene defined by one wild voice",
    image: "/img/culture/art.webp",
    teaser:
      "From backyard installations to gallery takeovers, a new wave of self-organised artists is pushing figurative painting into public space. We look at the movement, the collectors watching it, and why regional scenes matter again.",
    source: "olaoluslawn.com",
    to: "/culture/slawn",
  },
  {
    id: "fashion",
    category: "Fashion",
    title: "Silhouettes to know for the upcoming season",
    image: "/img/culture/fashion.webp",
    teaser:
      "Runway shows this season leaned into softer tailoring, technical outerwear, and a return to statement footwear. A quick brief on the houses defining the direction and where to read the collection reviews in full.",
    source: "supreme.com / vogue.com / margiela / rickowens",
    to: "/culture/fashion",
  },
  {
    id: "music",
    category: "Music",
    title: "The albums bending genres this season",
    image: "/img/culture/music_.jpg",
    teaser:
      "Boundary-blurring records from emerging artists are quietly dominating rotation. A quick roundup of the projects worth long-form listens — and where to find the write-ups tracking the charts week by week.",
    source: "billboard.com / awge.com / xxlmag.com",
    to: "/culture/music",
  },
  {
    id: "invest",
    category: "Invest",
    title: "Money literacy for young creators",
    image: "/img/culture/invest.jpg",
    teaser:
      "Understanding the markets is part of building a career that outlasts a hype cycle. Free tools from Google Finance and deeper research at Fidelity make a strong starter kit for anyone building.",
    source: "google.com/finance / fidelity.com",
    to: "/culture/invest",
  },
];

const Culture = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState("student");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    instagram: "",
    otherSocial: "",
    school: "",
    profession: "",
    interests: "",
    reason: "",
    work: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    document.title = "TMM|CULTURE";
  }, []);

  const update = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "file" ? e.target.files?.[0] || null : e.target.value,
    }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "That email doesn't look right.";
    if (!form.reason.trim())
      next.reason = "Tell us why you want to join.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const key = "tmm_community_signups";
      const prev = JSON.parse(localStorage.getItem(key) || "[]");
      prev.push({
        role,
        ...form,
        work: form.work ? { name: form.work.name, size: form.work.size } : null,
        ts: new Date().toISOString(),
      });
      localStorage.setItem(key, JSON.stringify(prev));
    } catch {
      /* ignore */
    }
    setSubmitted(true);
  };

  const roles = [
    { id: "student", label: "Student", Icon: GraduationCap, sub: "College or in-school" },
    { id: "professional", label: "Professional", Icon: Briefcase, sub: "Working in industry" },
    { id: "interested", label: "Enthusiast", Icon: Sparkles, sub: "Interested user" },
  ];

  return (
    <>
      <Header hoverColor={pageColors.culture} compact={false} />

      <div className="culture-page">
        <div className="culture-header">
          <img src={cultureMark} alt="Culture and Community" className="culture-mark" />
          <div className="culture-caption">Culture and Community</div>
          <div className="culture-cta-row">
            <button
              type="button"
              className="culture-cta"
              onClick={() => {
                const el = document.getElementById("stories");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Culture
            </button>
            <button
              type="button"
              className="culture-cta"
              onClick={() => {
                const el = document.getElementById("signup");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Community
            </button>
          </div>
        </div>

        {/* Stories grid */}
        <section className="culture-stories" id="stories">
          {stories.map((s) => (
            <button
              key={s.id}
              type="button"
              className="culture-card"
              onClick={() => navigate(s.to)}
              aria-label={`Open ${s.category} article`}
            >
              <img src={s.image} className="culture-card-img" alt={s.category} />
              <div className="culture-card-body">
                <div className="culture-card-tag">{s.category}</div>
                <h2 className="culture-card-title">{s.title}</h2>
                <p className="culture-card-teaser">{s.teaser}</p>
                <div className="culture-card-source">
                  Open the {s.category.toLowerCase()} roundup
                  <ArrowUpRight size={16} />
                </div>
                <div className="culture-card-note">
                  Sources: {s.source}
                </div>
              </div>
            </button>
          ))}
        </section>

        {/* Sign up */}
        <section className="signup-section" id="signup">
          <div className="signup-hero">
            <h2 className="signup-title">JOIN THE COMMUNITY</h2>
            <p className="signup-sub">
              Students, industry professionals, and curious minds welcome — tell us
              a bit about yourself.
            </p>
          </div>

          {submitted ? (
            <div className="signup-success">
              <CheckCircle2 size={36} />
              <h3>Welcome to The Monde Mania</h3>
              <p>
                Thanks {form.name.split(" ")[0] || "friend"} — we'll be in touch at{" "}
                <strong>{form.email}</strong> soon.
              </p>
              <button
                className="signup-reset"
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    name: "",
                    email: "",
                    phone: "",
                    instagram: "",
                    otherSocial: "",
                    school: "",
                    profession: "",
                    interests: "",
                    reason: "",
                    work: null,
                  });
                }}
              >
                Sign up someone else
              </button>
            </div>
          ) : (
            <form className="signup-form" onSubmit={onSubmit} noValidate>
              <div className="role-picker">
                {roles.map((r) => {
                  const Icon = r.Icon;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      className={`role-chip ${role === r.id ? "active" : ""}`}
                      onClick={() => setRole(r.id)}
                    >
                      <Icon size={20} />
                      <div>
                        <div className="role-label">{r.label}</div>
                        <div className="role-sub">{r.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="signup-row">
                <label className="signup-field">
                  <span>Full name*</span>
                  <input value={form.name} onChange={update("name")} placeholder="Your name" aria-invalid={!!errors.name} />
                  {errors.name && <em>{errors.name}</em>}
                </label>
                <label className="signup-field">
                  <span>Email*</span>
                  <input type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" aria-invalid={!!errors.email} />
                  {errors.email && <em>{errors.email}</em>}
                </label>
              </div>

              <div className="signup-row">
                <label className="signup-field">
                  <span>Phone (optional)</span>
                  <input type="tel" value={form.phone} onChange={update("phone")} placeholder="+1 555 123 4567" />
                </label>
                <label className="signup-field">
                  <span>Instagram (optional)</span>
                  <input value={form.instagram} onChange={update("instagram")} placeholder="@yourhandle" />
                </label>
              </div>

              <label className="signup-field">
                <span>Other socials (optional)</span>
                <input value={form.otherSocial} onChange={update("otherSocial")} placeholder="Twitter / TikTok / Behance / SoundCloud links" />
              </label>

              {role === "student" && (
                <label className="signup-field">
                  <span>College or school</span>
                  <input value={form.school} onChange={update("school")} placeholder="Where do you study?" />
                </label>
              )}
              {role === "professional" && (
                <label className="signup-field">
                  <span>Profession / company</span>
                  <input value={form.profession} onChange={update("profession")} placeholder="Role, studio, label, agency…" />
                </label>
              )}

              <label className="signup-field">
                <span>Interests</span>
                <input value={form.interests} onChange={update("interests")} placeholder="Music, fashion, art, events, community organising…" />
              </label>

              <label className="signup-field">
                <span>Why do you want to join?*</span>
                <textarea rows={4} value={form.reason} onChange={update("reason")} placeholder="Tell us what draws you to TMM." aria-invalid={!!errors.reason} />
                {errors.reason && <em>{errors.reason}</em>}
              </label>

              <label className="signup-field signup-upload">
                <span>Upload work (optional)</span>
                <div className="upload-box">
                  <Upload size={18} />
                  <span className="upload-name">
                    {form.work ? form.work.name : "PDF, image, or zip up to 20 MB"}
                  </span>
                  <input type="file" onChange={update("work")} accept=".pdf,image/*,.zip" />
                </div>
              </label>

              <button type="submit" className="signup-submit">Submit application</button>
              <p className="signup-note">
                Applications are stored on this device until a real backend is
                connected — no data leaves the browser yet.
              </p>
            </form>
          )}
        </section>
      </div>
    </>
  );
};

export default Culture;
